import { useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

interface UseDraftManagementReturn {
	deleteDialogOpen: boolean;
	draftToDelete: string | null;
	deleting: boolean;
	handleDeleteDraft: (draftId: string, event: React.MouseEvent) => void;
	confirmDeleteDraft: () => Promise<void>;
	cancelDeleteDraft: () => void;
}

export function useDraftManagement(
	onDraftDeleted: (draftId: string) => void,
): UseDraftManagementReturn {
	const { user } = useAuth();
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [draftToDelete, setDraftToDelete] = useState<string | null>(null);
	const [deleting, setDeleting] = useState(false);

	const handleDeleteDraft = useCallback(
		(draftId: string, event: React.MouseEvent) => {
			event.preventDefault();
			event.stopPropagation();
			setDraftToDelete(draftId);
			setDeleteDialogOpen(true);
		},
		[],
	);

	const confirmDeleteDraft = useCallback(async () => {
		if (!draftToDelete || !user?.id) return;

		setDeleting(true);
		try {
			const { error } = await supabase
				.from("reflections")
				.delete()
				.eq("id", draftToDelete)
				.eq("user_id", user.id);

			if (error) throw error;

			onDraftDeleted(draftToDelete);
			setDeleteDialogOpen(false);
			setDraftToDelete(null);
		} catch (error) {
			console.error("Error deleting draft:", error);
			alert("Failed to delete draft. Please try again.");
		} finally {
			setDeleting(false);
		}
	}, [draftToDelete, user, onDraftDeleted]);

	const cancelDeleteDraft = useCallback(() => {
		setDeleteDialogOpen(false);
		setDraftToDelete(null);
	}, []);

	return {
		deleteDialogOpen,
		draftToDelete,
		deleting,
		handleDeleteDraft,
		confirmDeleteDraft,
		cancelDeleteDraft,
	};
}
